import { IonDatetime, IonModal, useIonAlert } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useState } from 'react'
import { formatShortDate } from '../../helpers/timesheet'

export type DateRange = { startTime: string; endTime: string }
type RangeEdge = 'start' | 'end'

/** New range from editing one edge; null when start would pass end (v1 alert). */
export const applyRangeEdit = (
  range: DateRange,
  edge: RangeEdge,
  pickedISO: string,
): DateRange | null => {
  // Parse Y-M-D as a LOCAL date (new Date('2026-07-01') would be UTC)
  const [y, m, d] = pickedISO.split('T')[0].split('-').map(Number)
  const picked = new Date(y, (m ?? 1) - 1, d ?? 1)
  if (!y || Number.isNaN(picked.getTime())) return range
  const next = { ...range }
  if (edge === 'start') {
    picked.setHours(0, 0, 0, 0)
    next.startTime = picked.toISOString()
  } else {
    picked.setHours(23, 59, 59, 999)
    next.endTime = picked.toISOString()
  }
  if (new Date(next.startTime) > new Date(next.endTime)) return null
  return next
}

/**
 * Start/end range for the timesheet (v1 parity): each date is tapped and
 * changed independently via the wheel picker.
 */
export const DateRangePicker = ({
  range,
  onRangeChange,
}: {
  range: DateRange
  onRangeChange: (range: DateRange) => void
}) => {
  const { _ } = useLingui()
  const [editing, setEditing] = useState<RangeEdge | null>(null)
  const [presentAlert] = useIonAlert()

  const handleDateChange = (value: string | string[] | null | undefined) => {
    if (!value || !editing) return
    const picked = Array.isArray(value) ? value[0] : value
    const next = applyRangeEdit(range, editing, picked)
    if (next === null) {
      presentAlert({
        header: _(msg`Invalid Date Range`),
        message: _(msg`Start date must be before end date`),
        buttons: ['OK'],
      })
      return
    }
    onRangeChange(next)
  }

  const editingDate = new Date(
    editing === 'end' ? range.endTime : range.startTime,
  )
  // Local Y-M-D (toISOString is UTC and can shift a day)
  const editingValue = [
    editingDate.getFullYear(),
    String(editingDate.getMonth() + 1).padStart(2, '0'),
    String(editingDate.getDate()).padStart(2, '0'),
  ].join('-')

  return (
    <>
      <div className='tc-history-block'>
        <div className='tc-history-heading'>{_(msg`Dates`)}</div>
        <div className='tc-history-dates'>
          <button
            type='button'
            className='tc-history-date-btn'
            onClick={() => setEditing('start')}
          >
            {formatShortDate(range.startTime)}
          </button>
          <span className='tc-history-dates-sep'>–</span>
          <button
            type='button'
            className='tc-history-date-btn'
            onClick={() => setEditing('end')}
          >
            {formatShortDate(range.endTime)}
          </button>
        </div>
      </div>

      <IonModal
        isOpen={editing !== null}
        onDidDismiss={() => setEditing(null)}
        className='tc-date-modal'
      >
        <IonDatetime
          presentation='date'
          preferWheel
          value={editingValue}
          onIonChange={(e) => handleDateChange(e.detail.value)}
          firstDayOfWeek={1}
          showDefaultButtons
          doneText={_(msg`Done`)}
          cancelText={_(msg`Cancel`)}
          className='tc-date-picker'
          onIonCancel={() => setEditing(null)}
        />
      </IonModal>
    </>
  )
}
